import React from 'react';
import {
  useTripModal,
  isFolderModal,
  isActivityModal,
  isDestinationModal,
  isDayModal,
  isPhotoModal,
  isItineraryModal,
  isTripModal
} from '../contexts/modal-context-trip';
import { useTrip } from '../contexts/trip-context';

// Import your modal components
import AddActivityModal from './activity/add-activity-modal';
import AddActivityFromImageModal from './activity/add-activity-from-image-modal';
import EditActivityModal from './activity/edit-activity-modal';
import DeleteActivityModal from './activity/delete-activity-modal';
import MoveActivityToTripModal from './activity/move-activity-to-trip-modal';
import MoveActivityToFolderModal from './activity/move-activity-to-folder-modal';
import CopyActivityToFolderModal from './activity/copy-activity-to-folder-modal';
import ActivityCommentModal from './activity/activity-comment-modal';
import AddFolderModal from './folder/add-folder-modal';
import DeleteFolderModal from './folder/delete-folder-modal';
import EditFolderModal from './settings/edit-folder-modal';
import MoveFolderModal from './folder/move-folder-modal';
import MoveFolderToTripModal from '../trips/modals/move-folder-to-trip-modal';
import AddDayModal from './day/add-day-modal';
import EditDayModal from './day/edit-day-modal';
import MoveDayModal from './day/move-day-modal';
import MoveDestinationModal from './destination/move-destination-modal';
import AddTripPhotoModal from './photos/add-trip-photo';
import AddItineraryModal from './itinerary/add-itinerary-modal';
import TripInfoModal from './trip/trip-info-modal';
import { ITripActivity } from '../../Models/trips/trip-activity.model';

const TripModalWrapper: React.FC = () => {
  const { modalState, closeModal } = useTripModal();
  const { trip, setRefreshTrip } = useTrip();

  if (!modalState.show) return null;

  const { modalType } = modalState;

  // Helper function to handle modal close
  const handleModalClose = () => {
    closeModal();
  };

  // Folder Modals
  if (isFolderModal(modalState)) {
    switch (modalType) {
      case 'add':
        return (
          <AddFolderModal
            show={true}
            handleClose={handleModalClose}
          />
        );
      case 'edit':
        return (
          <EditFolderModal
            show={true}
            handleClose={handleModalClose}
          />
        );
      case 'delete':
        return (
          <DeleteFolderModal
            show={true}
            handleClose={handleModalClose}
            folder={modalState.data || null}
            onSuccess={() => {
              // Refresh trip data after successful deletion
              setRefreshTrip(true);
            }}
          />
        );
      case 'copy':
        return (
          <MoveFolderModal
            show={true}
            handleClose={handleModalClose}
            tripId={trip?.id}
            destinationId={modalState.data?.id}
            folderId={modalState.data?.id}
            folderName={modalState.data?.title}
            deleteAfterMove={false}
          />
        );
      case 'move':
        return (
          <MoveFolderModal
            show={true}
            handleClose={handleModalClose}
            tripId={trip?.id}
            destinationId={modalState.data?.id}
            folderId={modalState.data?.id}
            folderName={modalState.data?.title}
            deleteAfterMove={true}
          />
        );
      case 'moveFolderToTrip':
        return <MoveFolderToTripModal />;
      default:
        return null;
    }
  }

  // Activity Modals
  if (isActivityModal(modalState)) {
    switch (modalType) {
      case 'add':
        return (
          <AddActivityModal
            show={true}
            handleClose={handleModalClose}
            bias={modalState.bias || undefined}
            place={
              modalState.place &&
              modalState.place.placesId &&
              typeof modalState.place.latitude === 'number' &&
              typeof modalState.place.longitude === 'number'
                ? {
                    placesId: modalState.place.placesId,
                    latitude: modalState.place.latitude,
                    longitude: modalState.place.longitude,
                    name: modalState.place.name ?? ''
                  }
                : undefined
            }
          />
        );
      case 'addFromImage':
        return (
          <AddActivityFromImageModal
            show={true}
            handleClose={handleModalClose}
            bias={modalState.bias || undefined}
            place={
              modalState.place &&
              modalState.place.placesId &&
              typeof modalState.place.latitude === 'number' &&
              typeof modalState.place.longitude === 'number'
                ? {
                    placesId: modalState.place.placesId,
                    latitude: modalState.place.latitude,
                    longitude: modalState.place.longitude,
                    name: modalState.place.name ?? ''
                  }
                : undefined
            }
          />
        );
      case 'edit':
        return (
          <EditActivityModal
            show={true}
            handleClose={handleModalClose}
            activityId={
              (modalState.data as ITripActivity | undefined)?.id || ''
            }
            tripId={trip?.id || ''}
          />
        );
      case 'copy':
        return (
          <MoveActivityToTripModal
            show={true}
            handleClose={handleModalClose}
          />
        );
      case 'move':
        return (
          <MoveActivityToTripModal
            show={true}
            handleClose={handleModalClose}
          />
        );
      case 'delete':
        return (
          <DeleteActivityModal
            show={true}
            handleClose={handleModalClose}
            activity={(modalState.data as ITripActivity | undefined) || null}
            onSuccess={() => {
              modalState.onSuccess?.();
            }}
          />
        );
      case 'moveToFolder':
        return (
          <MoveActivityToFolderModal
            show={true}
            handleClose={handleModalClose}
            activityId={
              (modalState.data as any)?.id ||
              (modalState.data as any)?._id ||
              ''
            }
            activityTitle={(modalState.data as any)?.title || 'Activity'}
            currentFolderId={(modalState.data as any)?.folderId || ''}
            currentDestinationId={(modalState.data as any)?.destinationId || ''}
          />
        );
      case 'copyToFolder':
        return (
          <CopyActivityToFolderModal
            show={true}
            handleClose={handleModalClose}
            activityId={
              (modalState.data as any)?.id ||
              (modalState.data as any)?._id ||
              ''
            }
            activityTitle={(modalState.data as any)?.title || 'Activity'}
            currentFolderId={(modalState.data as any)?.folderId || ''}
            currentDestinationId={(modalState.data as any)?.destinationId || ''}
          />
        );
      case 'comment':
        return (
          <ActivityCommentModal
            show={true}
            handleClose={handleModalClose}
          />
        );
      default:
        return null;
    }
  }

  // Destination Modals
  if (isDestinationModal(modalState)) {
    switch (modalType) {
      case 'edit':
        return (
          <EditFolderModal
            show={true}
            handleClose={handleModalClose}
          />
        );
      case 'copy':
        return (
          <MoveDestinationModal
            show={true}
            handleClose={handleModalClose}
            tripId={trip?.id}
            destinationId={modalState.data?.id}
            destinationName={modalState.data?.title}
            deleteAfterMove={false}
          />
        );
      case 'move':
        return (
          <MoveDestinationModal
            show={true}
            handleClose={handleModalClose}
            tripId={trip?.id}
            destinationId={modalState.data?.id}
            destinationName={modalState.data?.title}
            deleteAfterMove={true}
          />
        );
      case 'delete':
        return (
          <DeleteFolderModal
            show={true}
            handleClose={handleModalClose}
            folder={modalState.data || null}
            onSuccess={() => {
              setRefreshTrip(true);
            }}
          />
        );
      default:
        return null;
    }
  }

  // Day Modals
  if (isDayModal(modalState)) {
    switch (modalType) {
      case 'add':
        return (
          <AddDayModal
            show={true}
            handleClose={handleModalClose}
            dataCards={[]}
          />
        );
      case 'edit':
        return (
          <EditDayModal
            show={true}
            handleClose={handleModalClose}
          />
        );
      case 'move':
        return (
          <MoveDayModal
            show={true}
            handleClose={handleModalClose}
          />
        );
      default:
        return null;
    }
  }

  // Photo Modals
  if (isPhotoModal(modalState)) {
    switch (modalType) {
      case 'add':
      case 'addFromImage':
        return (
          <AddTripPhotoModal
            show={true}
            handleClose={handleModalClose}
            tripId={modalState.data?.tripId || trip?.id || ''}
            bias={modalState.bias || undefined}
            onSuccess={() => {
              setRefreshTrip(true);
            }}
          />
        );
      default:
        return null;
    }
  }

  // Itinerary Modals
  if (isItineraryModal(modalState)) {
    switch (modalType) {
      case 'add':
        return (
          <AddItineraryModal
            show={true}
            handleClose={handleModalClose}
          />
        );
      default:
        return null;
    }
  }

  // Trip Modals
  if (isTripModal(modalState)) {
    switch (modalType) {
      case 'tripInfo':
        return (
          <TripInfoModal
            show={true}
            handleClose={handleModalClose}
          />
        );
      default:
        return null;
    }
  }

  return null;
};

export default TripModalWrapper;
